import { SETORES, UNIDADES } from "@caint/data";
import { EixoChip, Header, MatBadge, MatSelect, PesoChip, RiskBadge, Row, Sheet } from "@caint/design-system";
import type { Momento, NivelMaturidade } from "@caint/engine";
import { ACCEPT_IMAGEM, ACCEPT_IMAGEM_PDF } from "@caint/shared";
import type { RespostaItem, Setor, TipoConstatacao } from "@caint/shared";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { AvaliacaoRemota, EvidenciaRemota } from "../lib/api.ts";
import { engine } from "../lib/engine.ts";
import { COR_MATURIDADE, RUBRICA_MATURIDADE } from "../lib/escalaMaturidade.ts";
import type { EvidenciaPendente } from "../persistence/evidenciaQueue.ts";
import type { ItemMerged } from "../state/useAppState.ts";

type EnviarEvidencia = (itemId: string, momento: Momento, arquivo: File) => Promise<string | null>;
type ExcluirEvidencia = (ev: EvidenciaRemota) => Promise<string | null>;
type SubstituirEvidencia = (ev: EvidenciaRemota, arquivo: File) => Promise<string | null>;
type RemoverPendente = (localId: string) => Promise<void>;

// Rubrica e cores da curva de maturidade vêm de `lib/escalaMaturidade` — fonte
// única, compartilhada com o Checklist desktop, o Plano de Ação e a impressão.

function eviKey(momento: Momento): "evidenciaMi" | "evidenciaMf" {
  return momento === "mi" ? "evidenciaMi" : "evidenciaMf";
}
function tipoKey(momento: Momento): "tipoMi" | "tipoMf" {
  return momento === "mi" ? "tipoMi" : "tipoMf";
}
function fonteKey(momento: Momento): "fonteMi" | "fonteMf" {
  return momento === "mi" ? "fonteMi" : "fonteMf";
}

/** Tipos de constatação (técnica de auditoria) e o que cada um pede no registro. */
const TIPOS_CONSTATACAO: { v: TipoConstatacao; rotulo: string; ajuda: string }[] = [
  {
    v: "documento",
    rotulo: "📄 Documento/registro existe",
    ajuda: "Cite/identifique o documento (e anexe, se possível).",
  },
  { v: "observacao", rotulo: "👁️ Observação direta", ajuda: "Descreva o que foi observado in loco." },
  {
    v: "declaracao",
    rotulo: "💬 Declaração (entrevista)",
    ajuda: "Resuma o que foi informado e por quem (cargo).",
  },
  {
    v: "ausencia",
    rotulo: "🚫 Ausência constatada",
    ajuda: "Diga o que foi procurado, onde, e quem confirmou a ausência.",
  },
];
/** Constatações sem objeto físico → pedem a fonte (cargo de quem confirmou). */
const PEDE_FONTE = new Set<TipoConstatacao>(["declaracao", "ausencia"]);
function matDe(item: ItemMerged, momento: Momento): NivelMaturidade | null {
  return item[momento] ?? null;
}
function classeRisco(valor: number | null): string {
  return (engine.nivelRisco(valor) ?? "RP").toLowerCase();
}
function semEvidencia(item: ItemMerged, momento: Momento): boolean {
  const mat = matDe(item, momento);
  return mat != null && mat !== "na" && (item[eviKey(momento)] ?? "").trim() === "";
}
function ehCoreCritico(item: ItemMerged, momento: Momento): boolean {
  const mat = matDe(item, momento);
  return item.peso === 3 && (mat === "0" || mat === "1");
}
function fundoScore(sc: number | null): string {
  if (sc == null) {
    return "var(--surface-2)";
  }
  if (sc < 0.46) {
    return "var(--danger-soft)";
  }
  return sc < 0.6 ? "var(--mat-2-soft)" : "var(--mat-3-soft)";
}

function SetorRow({
  setor,
  itens,
  momento,
  onOpen,
}: {
  setor: Setor;
  itens: ItemMerged[];
  momento: Momento;
  onOpen: (code: string) => void;
}) {
  const tem = itens.length > 0;
  const sc = tem ? engine.setorScore(itens, momento) : null;
  const preench = itens.filter((it) => matDe(it, momento) != null).length;
  const core = tem ? engine.contarCore(itens, 0, momento) + engine.contarCore(itens, 1, momento) : 0;
  return (
    <Row
      title={setor.nome}
      sub={
        tem
          ? `${setor.code} · ${preench}/${itens.length} preenchidos${core ? ` · ⛔ ${core} CORE` : ""}`
          : `${setor.code} · ${setor.itens} itens no instrumento`
      }
      onClick={tem ? () => onOpen(setor.code) : undefined}
      chevron={tem}
      right={
        tem ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: 6,
              background: fundoScore(sc),
              color: sc != null && sc < 0.46 ? "var(--danger)" : "var(--slate-700)",
            }}
          >
            {engine.num2(sc)}
          </span>
        ) : (
          <span className="spec">amostra</span>
        )
      }
    />
  );
}

function ItemCard({
  item,
  momento,
  onOpen,
}: { item: ItemMerged; momento: Momento; onOpen: (id: string) => void }) {
  const mat = matDe(item, momento);
  const resid = engine.riscoResidual(item, momento);
  const coreAlert = ehCoreCritico(item, momento);
  const needsEvi = semEvidencia(item, momento);
  const borda = coreAlert
    ? "1px solid var(--danger)"
    : needsEvi
      ? "1px solid var(--warning)"
      : "0.5px solid var(--border)";
  return (
    <button
      type="button"
      className="m-card pad"
      style={{
        textAlign: "left",
        cursor: "pointer",
        border: borda,
        width: "100%",
        color: "var(--text)",
        background: "var(--surface)",
      }}
      onClick={() => onOpen(item.id)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        <span className="chip chip-code" style={{ fontSize: 11 }}>
          {item.code}
        </span>
        <PesoChip peso={item.peso} />
        <EixoChip eixo={item.eixo} />
        {coreAlert && (
          <span
            className="m-pill"
            style={{ background: "var(--danger-soft)", color: "var(--danger)", fontSize: 11 }}
          >
            ⛔ crítico
          </span>
        )}
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.35, marginBottom: 10 }}>
        {item.pergunta}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MatBadge m={mat} />
          <EvidenciaFlag item={item} momento={momento} />
        </div>
        {resid != null && <RiskBadge value={resid} withVal />}
      </div>
    </button>
  );
}

/**
 * Selo glanceável do estado da evidência de um item (facilita ver, na lista,
 * quais itens preenchidos têm ou não evidência). Itens sem nota ou NA não pedem
 * evidência → sem selo.
 */
function EvidenciaFlag({ item, momento }: { item: ItemMerged; momento: Momento }) {
  const mat = matDe(item, momento);
  const tem = (item[eviKey(momento)] ?? "").trim() !== "";
  // Evidência registrada mas sem a nota → item incompleto (caso OBS.03/GOV.01).
  if (mat == null) {
    return tem ? (
      <span
        className="m-pill"
        style={{
          fontSize: 11,
          background: "var(--warning-soft)",
          color: "color-mix(in srgb, var(--warning) 72%, black)",
        }}
      >
        ⚠ falta a nota
      </span>
    ) : null;
  }
  if (mat === "na") {
    return null;
  }
  return (
    <span
      className="m-pill"
      style={{
        fontSize: 11,
        background: tem ? "var(--mat-3-soft)" : "var(--warning-soft)",
        color: tem ? "var(--mat-4)" : "color-mix(in srgb, var(--warning) 72%, black)",
      }}
    >
      {tem ? "✓ evidência" : "⚠ sem evidência"}
    </span>
  );
}

export interface ChecklistProps {
  items: ItemMerged[];
  momento: Momento;
  setMomento: (m: Momento) => void;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  toast: (msg: string) => void;
  evidencias: Record<string, EvidenciaRemota[]>;
  evidenciasPendentes: Record<string, EvidenciaPendente[]>;
  enviarEvidencia: EnviarEvidencia;
  excluirEvidencia: ExcluirEvidencia;
  substituirEvidencia: SubstituirEvidencia;
  removerEvidenciaPendente: RemoverPendente;
  editavel: boolean;
  avaliacaoAtual?: AvaliacaoRemota;
  /** Setores ativos no escopo (recorte do checklist; doc 17b §3.3). */
  setoresAtivos: readonly string[];
  /** Reabre a tela de escopo (Setores da Unidade). */
  onEditarEscopo?: () => void;
  /** Abre a versão imprimível do setor (contingência offline). */
  onImprimir?: (setorCode: string) => void;
}

function nomeUnidade(unidadeId?: string): string {
  return UNIDADES.find((u) => u.id === unidadeId)?.nome ?? "Unidade";
}

export function Checklist({
  items,
  momento,
  patch,
  toast,
  evidencias,
  evidenciasPendentes,
  enviarEvidencia,
  excluirEvidencia,
  substituirEvidencia,
  removerEvidenciaPendente,
  editavel,
  avaliacaoAtual,
  setoresAtivos,
  onEditarEscopo,
  onImprimir,
}: ChecklistProps) {
  const [setor, setSetor] = useState<string | null>(null);
  const [itemAberto, setItemAberto] = useState<string | null>(null);

  const ativos = useMemo(() => new Set(setoresAtivos), [setoresAtivos]);
  const setoresAtivosLista = useMemo(() => SETORES.filter((s) => ativos.has(s.code)), [ativos]);
  const setoresInativos = useMemo(() => SETORES.filter((s) => !ativos.has(s.code)), [ativos]);

  const itensDoSetor = (code: string) => items.filter((it) => it.sector === code);

  if (!setor) {
    return (
      <Fragment>
        <Header title="Checklist" sub={nomeUnidade(avaliacaoAtual?.unidadeId)} />
        <div className="m-body">
          {!editavel && (
            <div
              className="m-card pad"
              style={{
                marginBottom: 12,
                background: "var(--surface-2)",
                fontSize: 12.5,
                color: "var(--text-muted)",
              }}
            >
              👁️ Somente leitura — esta unidade não tem ciclo aberto na janela atual.
            </div>
          )}
          {onEditarEscopo && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "2px 2px 8px",
              }}
            >
              <span className="spec">
                {setoresAtivosLista.length} de {SETORES.length} setores nesta unidade
              </span>
              <button type="button" className="m-h-action" onClick={onEditarEscopo}>
                Editar setores
              </button>
            </div>
          )}
          <div className="m-list">
            {setoresAtivosLista.map((s) => (
              <SetorRow
                key={s.code}
                setor={s}
                itens={itensDoSetor(s.code)}
                momento={momento}
                onOpen={setSetor}
              />
            ))}
          </div>
          {setoresInativos.length > 0 && (
            <div className="m-list" style={{ marginTop: 10 }}>
              {setoresInativos.map((s) => (
                <Row
                  key={s.code}
                  title={s.nome}
                  sub={`${s.code} · setor não existente na unidade`}
                  right={<span className="spec">fora de escopo</span>}
                />
              ))}
            </div>
          )}
        </div>
      </Fragment>
    );
  }

  const meta = SETORES.find((s) => s.code === setor);
  const cur = itensDoSetor(setor);
  const aberto = itemAberto ? items.find((i) => i.id === itemAberto) : undefined;

  return (
    <Fragment>
      <Header
        title={meta?.nome ?? setor}
        sub={`${meta?.code} · Macro ${meta?.macro} · score ${engine.num2(engine.setorScore(cur, momento))}`}
        onBack={() => setSetor(null)}
        action={
          onImprimir ? (
            <button type="button" className="m-h-action" onClick={() => onImprimir(setor)}>
              Imprimir
            </button>
          ) : undefined
        }
      />
      <div className="m-body">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {cur.map((it) => (
            <ItemCard key={it.id} item={it} momento={momento} onOpen={setItemAberto} />
          ))}
        </div>
      </div>

      {aberto && (
        <ItemSheet
          item={aberto}
          momento={momento}
          patch={patch}
          onClose={() => setItemAberto(null)}
          toast={toast}
          evis={evidencias[aberto.id] ?? []}
          pendentes={evidenciasPendentes[aberto.id] ?? []}
          enviarEvidencia={enviarEvidencia}
          excluirEvidencia={excluirEvidencia}
          substituirEvidencia={substituirEvidencia}
          removerEvidenciaPendente={removerEvidenciaPendente}
          editavel={editavel}
        />
      )}
    </Fragment>
  );
}

function MatLegenda({ mat }: { mat: NivelMaturidade | null }) {
  const niveis: NivelMaturidade[] = ["0", "1", "2", "3", "4", "na"];
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
      {niveis.map((k) => {
        const on = mat === k;
        return (
          <div
            key={k}
            style={{
              display: "flex",
              gap: 9,
              alignItems: "flex-start",
              padding: "7px 9px",
              borderRadius: 9,
              background: on ? "var(--surface-2)" : "transparent",
              border: on ? "1px solid var(--border)" : "1px solid transparent",
            }}
          >
            <span
              style={{
                flex: "0 0 auto",
                width: 22,
                height: 22,
                borderRadius: 6,
                background: COR_MATURIDADE[k],
                color: "#fff",
                fontFamily: "var(--font-mono)",
                fontWeight: 800,
                fontSize: 11,
                display: "grid",
                placeItems: "center",
              }}
            >
              {k === "na" ? "NA" : k}
            </span>
            <span style={{ fontSize: 12, lineHeight: 1.3, color: on ? "var(--text)" : "var(--text-muted)" }}>
              <b style={{ color: on ? "var(--text)" : "var(--slate-600)" }}>{engine.situacao(k)}</b> —{" "}
              {RUBRICA_MATURIDADE[k]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RiscoReadout({ item, momento }: { item: ItemMerged; momento: Momento }) {
  const resid = engine.riscoResidual(item, momento);
  const inh = engine.riscoInerente(item);
  const lc = classeRisco(resid);
  const nivel = engine.nivelRisco(resid);
  return (
    <div className="m-card pad" style={{ background: "var(--surface-2)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div className="spec">Risco residual ({momento === "mi" ? "inicial" : "final"})</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
            <b
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 30,
                color: resid == null ? "var(--text-faint)" : `var(--risk-${lc})`,
              }}
            >
              {resid == null ? "—" : engine.num1(resid)}
            </b>
            {resid != null && <RiskBadge value={resid} />}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="spec">P×I inerente</div>
          <b style={{ fontFamily: "var(--font-mono)", fontSize: 22 }}>{inh}</b>
        </div>
      </div>
      {resid != null && resid >= 8 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "0.5px solid var(--border)",
            fontSize: 12.5,
            color: "color-mix(in srgb, var(--warning) 70%, black)",
          }}
        >
          ⚠️ Risco ≥ 8 → prioridade no Plano de Ação ({nivel === "RC" ? "15 dias" : "60 dias"}).
        </div>
      )}
    </div>
  );
}

/**
 * Lastro da nota (técnica de auditoria): tipo de constatação + registro textual
 * (sempre exigido p/ nota não-NA) + fonte quando não há objeto físico. O anexo
 * (foto/PDF) é separado e sempre opcional — uma ausência se registra, não se
 * fotografa.
 */
function LastroConstatacao({
  item,
  momento,
  mat,
  needsEvi,
  editavel,
  patch,
}: {
  item: ItemMerged;
  momento: Momento;
  mat: NivelMaturidade | null;
  needsEvi: boolean;
  editavel: boolean;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
}) {
  const evi = item[eviKey(momento)] ?? "";
  const tipo = (item[tipoKey(momento)] ?? null) as TipoConstatacao | null;
  const fonte = item[fonteKey(momento)] ?? "";
  const tipoMeta = TIPOS_CONSTATACAO.find((t) => t.v === tipo);
  return (
    <Fragment>
      {mat != null && mat !== "na" && (
        <div className="m-field">
          <label htmlFor="tipo-const">Tipo de constatação</label>
          <select
            id="tipo-const"
            className="m-select"
            value={tipo ?? ""}
            disabled={!editavel}
            onChange={(e) =>
              patch(item.id, { [tipoKey(momento)]: (e.target.value || null) as TipoConstatacao | null })
            }
          >
            <option value="">Selecione como a nota foi constatada…</option>
            {TIPOS_CONSTATACAO.map((t) => (
              <option key={t.v} value={t.v}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="m-field">
        <label htmlFor="evi">
          Registro da constatação {needsEvi && <span style={{ color: "var(--danger)" }}>· obrigatório</span>}
        </label>
        <textarea
          id="evi"
          className={`m-textarea${needsEvi ? " missing" : ""}`}
          value={evi}
          disabled={!editavel}
          placeholder={
            tipoMeta?.ajuda ?? "Descreva a base da nota (documento, observação ou ausência constatada)…"
          }
          onChange={(e) => patch(item.id, { [eviKey(momento)]: e.target.value })}
        />
      </div>

      {tipo != null && PEDE_FONTE.has(tipo) && (
        <div className="m-field">
          <label htmlFor="fonte-const">Fonte — cargo de quem confirmou</label>
          <input
            id="fonte-const"
            className="m-input"
            value={fonte}
            disabled={!editavel}
            placeholder="Ex.: Enfermeiro(a) responsável do setor"
            onChange={(e) => patch(item.id, { [fonteKey(momento)]: e.target.value })}
          />
        </div>
      )}
    </Fragment>
  );
}

interface ItemSheetProps {
  item: ItemMerged;
  momento: Momento;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  onClose: () => void;
  toast: (msg: string) => void;
  evis: EvidenciaRemota[];
  pendentes: EvidenciaPendente[];
  enviarEvidencia: EnviarEvidencia;
  excluirEvidencia: ExcluirEvidencia;
  substituirEvidencia: SubstituirEvidencia;
  removerEvidenciaPendente: RemoverPendente;
  editavel: boolean;
}

function ItemSheet({
  item,
  momento,
  patch,
  onClose,
  toast,
  evis,
  pendentes,
  enviarEvidencia,
  excluirEvidencia,
  substituirEvidencia,
  removerEvidenciaPendente,
  editavel,
}: ItemSheetProps) {
  const mat = matDe(item, momento);
  const score = engine.score(mat);
  const inh = engine.riscoInerente(item);
  const needsEvi = semEvidencia(item, momento);
  const coreAlert = ehCoreCritico(item, momento);
  const fileRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  // Foto sendo trocada (dispara o mesmo <input>, mas o onChange substitui em vez
  // de anexar). null ⇒ o próximo arquivo é uma anexação nova.
  const [substituindo, setSubstituindo] = useState<EvidenciaRemota | null>(null);
  // Id da foto remota em processo de exclusão (evita duplo toque).
  const [excluindo, setExcluindo] = useState<string | null>(null);

  // Pré-visualização local das pendentes (object URLs revogados na limpeza).
  const previews = useMemo(
    () =>
      pendentes.map((p) => ({
        localId: p.localId,
        url: URL.createObjectURL(p.blob),
        img: p.contentType.startsWith("image/"),
        erro: p.erro,
      })),
    [pendentes],
  );
  useEffect(
    () => () => {
      for (const p of previews) {
        URL.revokeObjectURL(p.url);
      }
    },
    [previews],
  );

  const aoEscolher = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ""; // permite reanexar o mesmo arquivo
    const alvo = substituindo;
    setSubstituindo(null);
    if (!arquivo) {
      return;
    }
    setEnviando(true);
    const erro = alvo
      ? await substituirEvidencia(alvo, arquivo)
      : await enviarEvidencia(item.id, momento, arquivo);
    setEnviando(false);
    toast(erro ?? (alvo ? "Foto substituída ✓" : "Evidência anexada ✓"));
  };

  const pedirSubstituicao = (ev: EvidenciaRemota) => {
    setSubstituindo(ev);
    fileRef.current?.click();
  };

  const aoExcluir = async (ev: EvidenciaRemota) => {
    if (!confirm("Excluir esta foto? A ação registra quem removeu (auditoria).")) {
      return;
    }
    setExcluindo(ev.id);
    const erro = await excluirEvidencia(ev);
    setExcluindo(null);
    toast(erro ?? "Foto removida ✓");
  };

  const aoCancelarPendente = async (localId: string) => {
    await removerEvidenciaPendente(localId);
    toast("Captura cancelada ✓");
  };

  // Trava de obrigatoriedade (simétrica): um item só sai do estado "meio
  // preenchido". Bloqueia salvar/fechar quando: (a) há nota (≠ NA) sem registro
  // da constatação, OU (b) há registro mas sem a nota. Evita os dois casos que
  // geravam itens incompletos (nota sem evidência e evidência sem nota).
  const temEviTexto = (item[eviKey(momento)] ?? "").trim() !== "";
  const faltaNota = temEviTexto && mat == null; // escreveu a evidência, não pontuou
  const pendente = editavel && (needsEvi || faltaNota);
  const motivoPendencia = needsEvi
    ? "Registre a evidência da constatação (ou marque NA) para salvar."
    : "Selecione a maturidade observada para salvar.";
  const fechar = () => {
    if (pendente) {
      toast(motivoPendencia);
      return;
    }
    onClose();
  };
  // Botão de ação sobreposto à miniatura (barra inferior translúcida).
  const acaoFoto: React.CSSProperties = {
    flex: 1,
    border: "none",
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontSize: 13,
    lineHeight: 1,
    padding: "5px 0",
    cursor: "pointer",
  };
  return (
    <Sheet
      title={item.code}
      sub={`${item.processo} · ${item.norma}`}
      onClose={fechar}
      foot={
        <div style={{ display: "grid", gap: 6 }}>
          <button
            type="button"
            className="m-btn primary"
            disabled={!editavel || pendente}
            onClick={() => {
              toast("Item salvo ✓");
              onClose();
            }}
          >
            {needsEvi ? "⚠ Evidência obrigatória" : faltaNota ? "⚠ Selecione a maturidade" : "Salvar item"}
          </button>
          {pendente && (
            <span style={{ fontSize: 12, color: "var(--warning)", textAlign: "center" }}>
              {motivoPendencia}
            </span>
          )}
        </div>
      }
    >
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        <PesoChip peso={item.peso} />
        <EixoChip eixo={item.eixo} />
        <span className="chip chip-code" style={{ fontSize: 11 }}>
          P×I {inh}
        </span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>{item.pergunta}</div>
      <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 18 }}>
        <b>Evidência sugerida:</b> {item.evidencia}
      </div>

      {!editavel && (
        <div
          className="m-card pad"
          style={{
            marginBottom: 12,
            background: "var(--surface-2)",
            fontSize: 12.5,
            color: "var(--text-muted)",
          }}
        >
          👁️ Somente leitura — sem ciclo aberto para esta unidade.
        </div>
      )}
      <span style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 8 }}>
        Maturidade observada
      </span>
      <div style={{ pointerEvents: editavel ? "auto" : "none", opacity: editavel ? 1 : 0.6 }}>
        <MatSelect value={mat} onChange={(v) => patch(item.id, { [momento]: v })} />
      </div>
      {mat != null && mat !== "na" && (
        <div style={{ marginTop: 10, textAlign: "center", fontSize: 13 }}>
          Score <b>{engine.num2(typeof score === "number" ? score : null)}</b> · conformidade{" "}
          <b>{engine.conformidade(mat)}</b>
        </div>
      )}
      <MatLegenda mat={mat} />
      {coreAlert && (
        <div
          className="m-card pad"
          style={{ marginTop: 12, background: "var(--danger-soft)", color: "var(--danger)", fontSize: 13 }}
        >
          ⛔ Item CORE em maturidade {mat} — trava o selo em ≤ 59,9%.
        </div>
      )}

      <LastroConstatacao
        item={item}
        momento={momento}
        mat={mat}
        needsEvi={needsEvi}
        editavel={editavel}
        patch={patch}
      />

      <div className="m-field">
        <label htmlFor="evi-foto">Anexar foto/PDF — opcional (não é exigido p/ ausência)</label>
        {/* Câmera: `capture` abre direto a câmera. Galeria/arquivo: SEM `capture`,
            para o iOS oferecer Fotos e Arquivos em vez de forçar a câmera.
            O `accept` lista os tipos um a um (em vez de `image/*`) para o iOS
            converter HEIC→JPEG sozinho — ver ACCEPT_IMAGEM em @caint/shared. */}
        <input
          ref={camRef}
          type="file"
          accept={ACCEPT_IMAGEM}
          capture="environment"
          hidden
          onChange={aoEscolher}
        />
        <input
          ref={fileRef}
          id="evi-foto"
          type="file"
          accept={ACCEPT_IMAGEM_PDF}
          hidden
          onChange={aoEscolher}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="m-btn"
            style={{ flex: 1 }}
            disabled={enviando || !editavel}
            onClick={() => camRef.current?.click()}
          >
            {enviando ? "Enviando…" : "📷 Câmera"}
          </button>
          <button
            type="button"
            className="m-btn"
            style={{ flex: 1 }}
            disabled={enviando || !editavel}
            onClick={() => fileRef.current?.click()}
          >
            {enviando ? "Enviando…" : "🖼️ Galeria / arquivo"}
          </button>
        </div>
        {evis.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginTop: 10,
            }}
          >
            {evis.map((ev) => {
              const removendo = excluindo === ev.id;
              return (
                <div
                  key={ev.id}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "0.5px solid var(--border)",
                    background: "var(--surface-2)",
                    opacity: removendo ? 0.5 : 1,
                  }}
                >
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ver em tamanho real"
                    style={{ display: "block", width: "100%", height: "100%" }}
                  >
                    {ev.contentType.startsWith("image/") ? (
                      <img
                        // Miniatura (~10 KB) quando existe; sem ela, a foto
                        // cheia. O `<a>` em volta continua abrindo o tamanho real.
                        src={ev.urlMiniatura ?? ev.url}
                        alt="evidência"
                        // `lazy`: só baixa quando entra na tela. Sem isto, abrir
                        // um item com muitas fotos puxa TODAS de uma vez.
                        // `width/height` reservam o espaço e evitam o "pulo" de
                        // layout enquanto carrega.
                        loading="lazy"
                        decoding="async"
                        width={120}
                        height={120}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <span
                        style={{
                          display: "grid",
                          placeItems: "center",
                          height: "100%",
                          fontSize: 11,
                          color: "var(--text-muted)",
                        }}
                      >
                        PDF
                      </span>
                    )}
                  </a>
                  {editavel && (
                    <div style={{ position: "absolute", inset: "auto 0 0 0", display: "flex" }}>
                      <button
                        type="button"
                        title="Substituir esta foto"
                        aria-label="Substituir foto"
                        disabled={enviando || removendo}
                        onClick={() => pedirSubstituicao(ev)}
                        style={acaoFoto}
                      >
                        🔁
                      </button>
                      <button
                        type="button"
                        title="Excluir esta foto"
                        aria-label="Excluir foto"
                        disabled={removendo}
                        onClick={() => aoExcluir(ev)}
                        style={{ ...acaoFoto, borderLeft: "0.5px solid rgba(255,255,255,0.25)" }}
                      >
                        {removendo ? "…" : "🗑"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {previews.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginTop: 10,
            }}
          >
            {previews.map((p) => (
              <div
                key={p.localId}
                title={p.erro ?? "Na fila — sobe sozinha ao reconectar."}
                style={{
                  position: "relative",
                  aspectRatio: "1",
                  borderRadius: 8,
                  overflow: "hidden",
                  border: `1px dashed var(${p.erro ? "--danger" : "--warning"})`,
                  background: "var(--surface-2)",
                }}
              >
                {p.img ? (
                  <img
                    src={p.url}
                    alt="evidência pendente"
                    style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }}
                  />
                ) : (
                  <span style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 11 }}>
                    PDF
                  </span>
                )}
                <span
                  style={{
                    position: "absolute",
                    inset: "auto 0 0 0",
                    background: `var(${p.erro ? "--danger" : "--warning"})`,
                    color: "#fff",
                    fontSize: 9,
                    textAlign: "center",
                    padding: "1px 0",
                  }}
                >
                  {p.erro ? "⚠ recusada" : "⏳ na fila"}
                </span>
                {editavel && (
                  <button
                    type="button"
                    title="Cancelar esta captura"
                    aria-label="Cancelar captura"
                    onClick={() => aoCancelarPendente(p.localId)}
                    style={{
                      position: "absolute",
                      top: 3,
                      right: 3,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "none",
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      fontSize: 12,
                      lineHeight: 1,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="spec" style={{ marginTop: 6 }}>
          Fotos/PDF vão para o armazenamento seguro (B2). Capturas offline ficam na fila (⏳) e sobem sozinhas
          ao reconectar. Uma captura marcada ⚠ foi recusada pelo servidor (toque nela para ver o motivo) e não
          atrapalha as demais — cancele-a no ✕ e anexe de novo.
        </div>
      </div>

      <RiscoReadout item={item} momento={momento} />
    </Sheet>
  );
}
